import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetWorkshops,
  GetActiveWorkshops,
  GetWorkshopById,
  CreateWorkshop,
  UpdateWorkshop,
  DeleteWorkshop,
  ProcessWorkshopItem,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const workshopQueryKeys = {
  workshops: () => ["workshops"] as const,
  activeWorkshops: () => ["workshops", "active"] as const,
};

export const invalidateWorkshopQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["workshops"] });
  queryClient.invalidateQueries({ queryKey: ["workshop"] });
};

export const useWorkshopsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: workshopQueryKeys.workshops(),
    queryFn: async () => unwrapApiResponse(await GetWorkshops()),
  });
};

export const useActiveWorkshopsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: workshopQueryKeys.activeWorkshops(),
    queryFn: async () => unwrapApiResponse(await GetActiveWorkshops()),
  });
};

export const useWorkshopByIdQuery = (
  id: string,
  deliveryDate?: string | null,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    enabled: !!id,
    ...options,
    queryKey: ["workshop", id, deliveryDate ?? null],
    queryFn: async () => unwrapApiResponse(await GetWorkshopById(id, deliveryDate)),
  });
};

export const useCreateWorkshopMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateWorkshop>[0]) =>
      CreateWorkshop(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWorkshopQueries(queryClient);
      }
    },
  });
};

export const useUpdateWorkshopMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateWorkshop>[0]) =>
      UpdateWorkshop(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWorkshopQueries(queryClient);
      }
    },
  });
};

export const useDeleteWorkshopMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteWorkshop>[0]) =>
      DeleteWorkshop(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWorkshopQueries(queryClient);
      }
    },
  });
};

export const useProcessWorkshopItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof ProcessWorkshopItem>[0]) =>
      ProcessWorkshopItem(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateWorkshopQueries(queryClient);
      }
    },
  });
};
